/**
 * /admin/messages — لوحة مراقبة عامّة لكلّ قوالب رسائل واتساب في الموقع.
 *
 * - الجزء الأعلى: جدول مراقبة (كلّ قالب: حالة، عدد إرسال ٣٠ يوم، آخر إرسال،
 *   toggle تفعيل/تعطيل، رابط لمكان التحرير الفعليّ).
 * - الجزء الأسفل: محرّرات النصوص للقوالب القابلة للتحرير هنا (الهديّة + footer).
 *
 * كلّ تبويب/صفحة أخرى يبقى مكان التحرير الأصليّ — هذه فقط نقطة المراقبة المركزيّة.
 */
import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Save, ExternalLink, Pencil } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "@/lib/toast";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useLocale } from "@/contexts/LocaleContext";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  listMessageTemplates,
  upsertMessageTemplate,
  getCampaignFooterEnabled,
  setCampaignFooterEnabled,
} from "@/utils/messageTemplates.functions";
import {
  listMessageRegistry,
  setMessageTemplateEnabled,
  type RegistryRow,
} from "@/utils/messageRegistry.functions";

const ALLOWED_TABS = ["gift", "footer", "order", "loyalty", "ambassador"] as const;
type AdminMessagesTab = (typeof ALLOWED_TABS)[number];

export const Route = createFileRoute("/admin/messages")({
  component: AdminMessagesPage,
  validateSearch: (search: Record<string, unknown>): { tab?: AdminMessagesTab; focus?: string } => {
    const t = search.tab;
    return typeof t === "string" && (ALLOWED_TABS as readonly string[]).includes(t)
      ? {
          tab: t as AdminMessagesTab,
          focus: typeof search.focus === "string" ? search.focus : undefined,
        }
      : {
          focus: typeof search.focus === "string" ? search.focus : undefined,
        };
  },
  head: () => ({
    meta: [
      { title: "WhatsApp Templates Monitor · Lecmo Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type TemplateCategory = "transactional" | "marketing" | "footer";

interface TemplateMeta {
  key: string;
  title_ar: string;
  title_en: string;
  hint_ar: string;
  hint_en: string;
  placeholders: string[];
  category: TemplateCategory;
}

function CategoryBadge({ category, isAr }: { category: TemplateCategory; isAr: boolean }) {
  if (category === "footer") {
    return (
      <Badge variant="outline" className="border-muted-foreground/30 text-muted-foreground">
        {isAr ? "🏷️ ذيل (يُلصق آلياً)" : "🏷️ Footer (auto-appended)"}
      </Badge>
    );
  }
  if (category === "transactional") {
    return (
      <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400">
        {isAr ? "🔒 معاملاتيّة — بلا فوتر" : "🔒 Transactional — no footer"}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400">
      {isAr ? "📣 تسويقيّة — مع فوتر" : "📣 Marketing — with footer"}
    </Badge>
  );
}

const EDITABLE_TEMPLATES: TemplateMeta[] = [
  {
    key: "gift_msg_recipient_created",
    title_ar: "إشعار المُهدى إليه — عند الطلب",
    title_en: "Gift recipient — order created",
    hint_ar: "يُرسَل للمُهدى إليه فور إتمام الطلب.",
    hint_en: "Sent to the recipient as soon as the order is placed.",
    placeholders: ["{recipient_name}", "{sender_name}", "{order_name}"],
    category: "transactional",
  },
  {
    key: "gift_msg_recipient_delivered",
    title_ar: "إشعار المُهدى إليه — عند التسليم",
    title_en: "Gift recipient — delivered",
    hint_ar: "يُرسَل للمُهدى إليه عند تأكيد التسليم.",
    hint_en: "Sent to the recipient when delivery is confirmed.",
    placeholders: ["{recipient_name}", "{sender_name}"],
    category: "transactional",
  },
  {
    key: "gift_msg_sender_delivered",
    title_ar: "إشعار المُهدي — عند التسليم",
    title_en: "Gift sender — delivered",
    hint_ar: "يُرسَل للمُهدي تأكيداً بأنّ هديّته وصلت.",
    hint_en: "Sent to the sender confirming the gift was delivered.",
    placeholders: ["{sender_name}", "{recipient_name}", "{order_name}"],
    category: "transactional",
  },
  {
    key: "whatsapp_unsub_footer",
    title_ar: "ذيل إلغاء الاشتراك (Footer)",
    title_en: "Unsubscribe footer",
    hint_ar: "يُضاف آليّاً لكلّ رسالة تسويقيّة (ليس للطلبات/الشحن/التسليم).",
    hint_en: "Auto-appended to marketing messages only (not transactional ones).",
    placeholders: ["{unsub_url}"],
    category: "footer",
  },
  {
    key: "utility_account_footer",
    title_ar: "فوتر «راجع حسابك» — واتساب",
    title_en: "“Check your account” footer — WhatsApp",
    hint_ar:
      "يُضاف آليّاً لكلّ رسائل Utility على واتساب (تأكيد طلب، شحن، تسليم، هديّة للمُهدي، نقاط، محفظة، ترقية، إنجاز…). يُستثنى المستلم في الهديّة. اتركه فارغاً لتعطيله. الرابط يتحوّل آليّاً إلى رابط قصير متتبَّع.",
    hint_en:
      "Auto-appended to every Utility WhatsApp message (order confirmation, shipping, delivery, gift sender, points, wallet, promotion, milestone…). Excluded for gift recipients. Leave empty to disable. Links auto-convert to tracked short links.",
    placeholders: [],
    category: "footer",
  },
  {
    key: "utility_account_footer_email",
    title_ar: "فوتر «راجع حسابك» — إيميل (HTML)",
    title_en: "“Check your account” footer — Email (HTML)",
    hint_ar:
      "يُضاف آليّاً لكلّ إيميلات Utility (نفس قائمة الواتساب). يُكتب بصيغة HTML. اتركه فارغاً لتعطيله.",
    hint_en:
      "Auto-appended to every Utility email (same scope as WhatsApp). Written as HTML. Leave empty to disable.",
    placeholders: [],
    category: "footer",
  },
  {
    key: "wallet_account_footer",
    title_ar: "فوتر المحفظة (مع الرصيد) — واتساب",
    title_en: "Wallet footer (with balance) — WhatsApp",
    hint_ar:
      "يُستخدم بدل الفوتر العامّ في رسائل النقاط/المحفظة (استبدال نقاط، نضوج رصيد، طلب سحب، قبول/رفض…). استخدم {balance} ليُستبدل آلياً برصيد العميل الفعليّ (نقاط + كاش AED).",
    hint_en:
      "Replaces the generic footer for points/wallet messages (redemption, matured credit, payouts…). Use {balance} to auto-insert the user's actual balance (points + AED cash).",
    placeholders: ["{balance}"],
    category: "footer",
  },
  {
    key: "wallet_account_footer_email",
    title_ar: "فوتر المحفظة (مع الرصيد) — إيميل (HTML)",
    title_en: "Wallet footer (with balance) — Email (HTML)",
    hint_ar:
      "نسخة HTML من فوتر المحفظة. استخدم {balance} ليُستبدل برصيد العميل. يُضاف لإيميلات النقاط/المحفظة فقط.",
    hint_en:
      "HTML variant of the wallet footer. Use {balance} to insert the user's balance. Email-only.",
    placeholders: ["{balance}"],
    category: "footer",
  },
  {
    key: "discover_footer",
    title_ar: "فوتر «اكتشف عطور Lecmo» — واتساب",
    title_en: "“Discover Lecmo” footer — WhatsApp",
    hint_ar:
      "يُضاف لرسائل المُهدى إليه (الذي قد لا يكون عضواً في الموقع) ليدعوه لتصفّح أجمل عطور Lecmo. اتركه فارغاً لتعطيله.",
    hint_en:
      "Appended to gift-recipient messages (who may not be members) to invite them to explore Lecmo's fragrances. Leave empty to disable.",
    placeholders: [],
    category: "footer",
  },
  {
    key: "discover_footer_email",
    title_ar: "فوتر «اكتشف عطور Lecmo» — إيميل (HTML)",
    title_en: "“Discover Lecmo” footer — Email (HTML)",
    hint_ar:
      "نسخة HTML من فوتر الاكتشاف للمُهدى إليه. اتركه فارغاً لتعطيله.",
    hint_en:
      "HTML variant of the discover footer for gift recipients. Leave empty to disable.",
    placeholders: [],
    category: "footer",
  },
  {
    key: "campaign_footer",
    title_ar: "فوتر حملة/مناسبة عامّة — واتساب",
    title_en: "Campaign / occasion footer — WhatsApp",
    hint_ar:
      "نصّ موسميّ عامّ (رمضان، عيد، يوم وطنيّ، إطلاق…). عند تفعيل المفتاح أعلى المحرّر يحلّ محلّ كلّ الفوترات الأخرى لكلّ الرسائل.",
    hint_en:
      "Seasonal / occasion footer (Ramadan, Eid, National Day, launch…). When the toggle above is on, it overrides every other footer on every message.",
    placeholders: [],
    category: "footer",
  },
  {
    key: "campaign_footer_email",
    title_ar: "فوتر حملة/مناسبة عامّة — إيميل (HTML)",
    title_en: "Campaign / occasion footer — Email (HTML)",
    hint_ar:
      "نسخة HTML من فوتر الحملة. يُكتب بصيغة HTML. اتركه فارغاً لتعطيل الإيميل فقط.",
    hint_en:
      "HTML variant of the campaign footer. Leave empty to disable for email only.",
    placeholders: [],
    category: "footer",
  },
];

const OTHER_EDITABLE_TEMPLATES: TemplateMeta[] = [
  {
    key: "order_confirmation",
    title_ar: "طلب — تأكيد على الواتساب",
    title_en: "Order — WhatsApp confirmation",
    hint_ar: "يُرسَل عند تأكيد الطلب (لا يوجد مُرسِل مفعَّل بعد — جاهز للتحرير).",
    hint_en: "Sent on order creation (no active sender yet — ready to edit).",
    placeholders: ["{customer_name}", "{order_name}", "{total_label}", "{track_url}"],
    category: "transactional",
  },
  {
    key: "order_paid",
    title_ar: "طلب — تأكيد الدفع",
    title_en: "Order — payment confirmed",
    hint_ar: "يُرسَل على الواتساب عند تحوّل الطلب إلى مدفوع في Shopify.",
    hint_en: "Sent on WhatsApp when the order becomes paid in Shopify.",
    placeholders: ["{customer_name}", "{order_name}", "{total_label}", "{track_url}"],
    category: "transactional",
  },
  {
    key: "order_shipped",
    title_ar: "طلب — شحن",
    title_en: "Order — shipped",
    hint_ar: "يُرسَل عند شحن الطلب.",
    hint_en: "Sent when the order ships.",
    placeholders: ["{customer_name}", "{order_name}", "{tracking_url}", "{tracking_number}", "{carrier}"],
    category: "transactional",
  },
  {
    key: "order_delivered",
    title_ar: "طلب — تسليم",
    title_en: "Order — delivered",
    hint_ar: "يُرسَل عند تأكيد التسليم.",
    hint_en: "Sent when delivery is confirmed.",
    placeholders: ["{customer_name}", "{order_name}"],
    category: "transactional",
  },
  {
    key: "order_cancelled",
    title_ar: "طلب — إلغاء",
    title_en: "Order — cancelled",
    hint_ar: "يُرسَل عند إلغاء الطلب من Shopify.",
    hint_en: "Sent when the order is cancelled in Shopify.",
    placeholders: ["{customer_name}", "{order_name}", "{reason}"],
    category: "transactional",
  },
  {
    key: "driver_out_for_delivery",
    title_ar: "توصيل محليّ — السائق في الطريق",
    title_en: "Local delivery — driver on the way",
    hint_ar: "يُرسَل عند تحديث سائقنا الحالة إلى «في الطريق».",
    hint_en: "Sent when our driver marks the order out for delivery.",
    placeholders: ["{customer_name}", "{order_name}"],
    category: "transactional",
  },
  {
    key: "driver_delivered",
    title_ar: "توصيل محليّ — تمّ التسليم",
    title_en: "Local delivery — delivered",
    hint_ar: "يُرسَل عند تأكيد سائقنا التسليم.",
    hint_en: "Sent when our driver confirms delivery.",
    placeholders: ["{customer_name}", "{order_name}"],
    category: "transactional",
  },
  {
    key: "loyalty_promotion",
    title_ar: "ولاء — ترقية شريحة",
    title_en: "Loyalty — tier promotion",
    hint_ar: "حين يصل العميل لشريحة جديدة (لا يوجد مُرسِل مفعَّل بعد — جاهز للتحرير).",
    hint_en: "When a customer reaches a new tier (no active sender yet — ready to edit).",
    placeholders: ["{customer_name}", "{tier_name}", "{benefits}"],
    category: "transactional",
  },
  {
    key: "loyalty_points_earned",
    title_ar: "ولاء — استبدال نقاط",
    title_en: "Loyalty — points redeemed",
    hint_ar: "يُرسَل بعد استبدال نقاط لكوبون.",
    hint_en: "Sent after redeeming points for a coupon.",
    placeholders: ["{customer_name}", "{points_spent}", "{coupon_code}", "{coupon_amount}", "{expires_at}"],
    category: "transactional",
  },
  {
    key: "wallet_deposit",
    title_ar: "محفظة — إيداع/مكافأة",
    title_en: "Wallet — deposit/reward",
    hint_ar: "إشعار السفير بإضافة رصيد (مكافأة معلَّقة، نضوج، استخدام كوبون…).",
    hint_en: "Notifies an ambassador of new credit (pending reward, matured, coupon redeemed…).",
    placeholders: ["{ambassador_name}", "{amount}", "{currency}", "{order_name}", "{kind_text}"],
    category: "transactional",
  },
  {
    key: "wallet_withdraw",
    title_ar: "محفظة — سحب",
    title_en: "Wallet — withdrawal",
    hint_ar: "حالة طلبات السحب (مطلوب/قيد المعالجة/مدفوع/مرفوض).",
    hint_en: "Withdrawal status (requested/processing/paid/rejected).",
    placeholders: ["{ambassador_name}", "{amount}", "{currency}", "{kind_text}", "{processing_days}", "{rejection_reason}"],
    category: "transactional",
  },
  {
    key: "ambassador_milestone",
    title_ar: "سفير — إنجاز Milestone",
    title_en: "Ambassador — milestone",
    hint_ar: "تهنئة السفير بإنجاز هدف (يُستخدم حالياً قالب «محفظة — إيداع»؛ هذا مستقلّ لاحقاً).",
    hint_en: "Congratulates an ambassador on a milestone (currently uses wallet-deposit template).",
    placeholders: ["{ambassador_name}", "{milestone_title}", "{reward_amount}", "{currency}"],
    category: "transactional",
  },
  // ───── طلبات السفراء (Super Star applications) ─────
  {
    key: "ambassador_app_received_subject",
    title_ar: "طلب سفير — عنوان إيميل الاستلام",
    title_en: "Ambassador application — received (email subject)",
    hint_ar: "عنوان الإيميل عند استلام طلب الترقّي. يدعم {ref}.",
    hint_en: "Email subject when application is received. Supports {ref}.",
    placeholders: ["{ref}"],
    category: "transactional",
  },
  {
    key: "ambassador_app_received_body",
    title_ar: "طلب سفير — نصّ رسالة الاستلام (واتساب + إيميل)",
    title_en: "Ambassador application — received (body)",
    hint_ar: "نصّ موحَّد للواتساب والإيميل. يجمع AR + EN مع فاصل تلقائياً.",
    hint_en: "Single body used for both WhatsApp and email. AR + EN combined automatically.",
    placeholders: ["{name}", "{ref}", "{status_url}", "{login_url}"],
    category: "transactional",
  },
  {
    key: "ambassador_app_approved_subject",
    title_ar: "طلب سفير — عنوان إيميل الموافقة",
    title_en: "Ambassador application — approved (email subject)",
    hint_ar: "عنوان الإيميل عند الموافقة. يدعم {ref}.",
    hint_en: "Email subject on approval. Supports {ref}.",
    placeholders: ["{ref}"],
    category: "transactional",
  },
  {
    key: "ambassador_app_approved_body",
    title_ar: "طلب سفير — نصّ رسالة الموافقة (واتساب + إيميل)",
    title_en: "Ambassador application — approved (body)",
    hint_ar: "نصّ موحَّد للواتساب والإيميل. {note} يبقى فارغاً إن لم تكتب ملاحظة.",
    hint_en: "Single body for WhatsApp + email. {note} stays blank when no admin note.",
    placeholders: ["{name}", "{ref}", "{note}", "{dashboard_url}", "{login_url}"],
    category: "transactional",
  },
  {
    key: "ambassador_app_rejected_subject",
    title_ar: "طلب سفير — عنوان إيميل الرفض",
    title_en: "Ambassador application — rejected (email subject)",
    hint_ar: "عنوان الإيميل عند الرفض. يدعم {ref}.",
    hint_en: "Email subject on rejection. Supports {ref}.",
    placeholders: ["{ref}"],
    category: "transactional",
  },
  {
    key: "ambassador_app_rejected_body",
    title_ar: "طلب سفير — نصّ رسالة الرفض (واتساب + إيميل)",
    title_en: "Ambassador application — rejected (body)",
    hint_ar: "نصّ موحَّد للواتساب والإيميل. ابقَ لطيفاً — العميل يبقى Lecmo Lover.",
    hint_en: "Single body for WhatsApp + email. Stay kind — user remains a Lecmo Lover.",
    placeholders: ["{name}", "{ref}", "{note}", "{status_url}", "{login_url}"],
    category: "transactional",
  },
  {
    key: "ambassador_app_needs_info_subject",
    title_ar: "طلب سفير — عنوان إيميل طلب معلومات",
    title_en: "Ambassador application — needs info (email subject)",
    hint_ar: "عنوان الإيميل حين تطلب معلومات إضافيّة. يدعم {ref}.",
    hint_en: "Email subject when more info is needed. Supports {ref}.",
    placeholders: ["{ref}"],
    category: "transactional",
  },
  {
    key: "ambassador_app_needs_info_body",
    title_ar: "طلب سفير — نصّ رسالة طلب معلومات (واتساب + إيميل)",
    title_en: "Ambassador application — needs info (body)",
    hint_ar: "نصّ موحَّد للواتساب والإيميل. اكتب في الملاحظة بالضبط ما تحتاجه.",
    hint_en: "Single body for WhatsApp + email. Write in the admin note exactly what you need.",
    placeholders: ["{name}", "{ref}", "{note}", "{status_url}", "{login_url}"],
    category: "transactional",
  },
];

const ALL_EDITABLE_TEMPLATES: TemplateMeta[] = [
  ...EDITABLE_TEMPLATES,
  ...OTHER_EDITABLE_TEMPLATES,
];

type TplBucket = "gift" | "footer" | "order" | "loyalty" | "ambassador";

function tplBucket(key: string): TplBucket {
  if (key.startsWith("gift_")) return "gift";
  if (
    key === "whatsapp_unsub_footer" ||
    key.endsWith("_footer") ||
    key.endsWith("_footer_email") ||
    key.startsWith("campaign_footer")
  ) {
    return "footer";
  }
  if (key.startsWith("order_")) return "order";
  if (key.startsWith("ambassador_")) return "ambassador";
  if (key.startsWith("loyalty_") || key.startsWith("wallet_")) return "loyalty";
  return "footer";
}

function AdminMessagesPage() {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useIsAdmin();
  const { dir } = useLocale();
  const isAr = dir === "rtl";
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const activeTab: AdminMessagesTab = search.tab ?? "footer";
  const qc = useQueryClient();
  const listFn = useServerFn(listMessageTemplates);
  const upsertFn = useServerFn(upsertMessageTemplate);
  const registryFn = useServerFn(listMessageRegistry);
  const toggleFn = useServerFn(setMessageTemplateEnabled);
  const getCampaignFn = useServerFn(getCampaignFooterEnabled);
  const setCampaignFn = useServerFn(setCampaignFooterEnabled);

  const ready = !!user && isAdmin;

  const { data: campaignData } = useQuery({
    queryKey: ["admin-campaign-footer-enabled"],
    queryFn: () => getCampaignFn(),
    enabled: ready,
  });

  const campaignToggle = useMutation({
    mutationFn: (enabled: boolean) => setCampaignFn({ data: { enabled } }),
    onSuccess: (_d, enabled) => {
      toast.success(
        enabled
          ? (isAr ? "تمّ تفعيل فوتر الحملة — يحلّ محلّ كلّ الفوترات الأخرى" : "Campaign footer enabled — overrides all others")
          : (isAr ? "تمّ تعطيل فوتر الحملة — عادت الفوترات العاديّة" : "Campaign footer disabled"),
      );
      qc.invalidateQueries({ queryKey: ["admin-campaign-footer-enabled"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const { data: templatesData, isLoading: tplLoading } = useQuery({
    queryKey: ["admin-message-templates"],
    queryFn: () => listFn(),
    enabled: ready,
  });

  const { data: registryData, isLoading: regLoading } = useQuery({
    queryKey: ["admin-message-registry"],
    queryFn: () => registryFn(),
    enabled: ready,
    refetchInterval: 60_000,
  });

  const upsert = useMutation({
    mutationFn: (input: { key: string; ar: string; en: string }) =>
      upsertFn({ data: input }),
    onSuccess: () => {
      toast.success(isAr ? "تمّ الحفظ" : "Saved");
      qc.invalidateQueries({ queryKey: ["admin-message-templates"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = useMutation({
    mutationFn: (input: { key: string; enabled: boolean }) =>
      toggleFn({ data: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-message-registry"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  React.useEffect(() => {
    if (tplLoading || !search.focus) return;

    let cancelled = false;
    let attempts = 0;

    const tryFocus = () => {
      if (cancelled) return;

      const el = document.getElementById(`tpl-${search.focus}`);
      if (!el) {
        if (attempts < 10) {
          attempts += 1;
          window.setTimeout(tryFocus, 80);
        }
        return;
      }

      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ring-2", "ring-primary", "rounded-none");
      const firstTextarea = el.querySelector("textarea");
      if (firstTextarea instanceof HTMLTextAreaElement) {
        window.setTimeout(() => firstTextarea.focus(), 120);
      }

      window.setTimeout(() => {
        el.classList.remove("ring-2", "ring-primary", "rounded-none");
      }, 2000);

      navigate({ search: { tab: activeTab }, replace: true });
    };

    tryFocus();

    return () => {
      cancelled = true;
    };
  }, [activeTab, navigate, search.focus, tplLoading]);

  const handleEditTemplate = React.useCallback(
    (templateKey: string) => {
      navigate({
        search: {
          tab: tplBucket(templateKey),
          focus: templateKey,
        },
        replace: false,
      });
    },
    [navigate],
  );

  if (authLoading || roleLoading) {
    return (
      <SiteShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SiteShell>
    );
  }

  if (!user || !isAdmin) {
    return (
      <SiteShell>
        <div className="container max-w-2xl py-16 text-center">
          <p className="text-muted-foreground">
            {isAr ? "هذه الصفحة للمسؤولين فقط." : "Admins only."}
          </p>
          <Link to="/" className="mt-4 inline-block text-sm underline">
            {isAr ? "العودة للرئيسيّة" : "Back to home"}
          </Link>
        </div>
      </SiteShell>
    );
  }

  const templatesMap = new Map<string, { ar: string; en: string }>(
    ((templatesData?.templates ?? []) as Array<{ key: string; value: { ar?: string; en?: string } }>).map((t) => [
      t.key,
      { ar: t.value?.ar ?? "", en: t.value?.en ?? "" },
    ]),
  );

  const rows = (registryData?.rows ?? []) as RegistryRow[];

  // group rows
  const groupedRows = React.useMemo(() => {
    const groups: Record<string, RegistryRow[]> = {};
    for (const r of rows) {
      groups[r.group] = groups[r.group] ?? [];
      groups[r.group].push(r);
    }
    return groups;
  }, [rows]);

  const groupLabels: Record<string, { ar: string; en: string }> = {
    gift: { ar: "الهديّة", en: "Gift" },
    footer: { ar: "ذيل الرسائل", en: "Footer" },
    reminder: { ar: "حملات التذكير", en: "Reminder Campaigns" },
    loyalty: { ar: "الولاء", en: "Loyalty" },
    wallet: { ar: "المحفظة", en: "Wallet" },
    order: { ar: "أحداث الطلبات", en: "Order Events" },
    ambassador: { ar: "السفراء", en: "Ambassadors" },
    seasonal: { ar: "البثوث الموسميّة", en: "Seasonal Broadcasts" },
  };

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
        <AdminNav />
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">
            {isAr ? "مراقبة قوالب واتساب" : "WhatsApp Templates Monitor"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isAr
              ? "كلّ قوالب الرسائل في الموقع في مكان واحد. التفعيل/التعطيل من هنا، والتحرير من مكانه الأصليّ."
              : "All site message templates in one place. Toggle here; edit in their original location."}
          </p>
        </header>

        {/* ─── بطاقة الفهرس/الشرح ─── */}
        <section className="mt-6 rounded-none border border-border bg-muted/30 p-4 text-xs leading-relaxed">
          <h2 className="mb-2 text-sm font-semibold">
            {isAr ? "كيف تقرأ هذا الفهرس؟" : "How to read this index"}
          </h2>
          <p className="text-muted-foreground">
            {isAr
              ? "كلّ صفّ هنا = نوع رسالة آليّة يرسلها الموقع. تحت كلّ عنوان ترى: القنوات (واتساب/إيميل) · المستلم · متى يُرسَل · نوع الفوتر الملحَق."
              : "Each row = an automatic message the site sends. Under each title: channels (WhatsApp/Email) · recipient · trigger · attached footer kind."}
          </p>
          <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
            <li>
              <Badge variant="outline" className="me-1.5 text-[10px]">📱 واتساب</Badge>
              <Badge variant="outline" className="text-[10px]">✉️ إيميل</Badge>
              <span className="ms-2 text-muted-foreground">{isAr ? "قنوات الإرسال" : "Channels"}</span>
            </li>
            <li>
              <Badge variant="outline" className="text-[10px]">{isAr ? "فوتر عامّ" : "Generic footer"}</Badge>
              <span className="ms-2 text-muted-foreground">{isAr ? "رابط حسابي فقط" : "Account link only"}</span>
            </li>
            <li>
              <Badge variant="outline" className="text-[10px]">{isAr ? "فوتر بالرصيد" : "Wallet footer"}</Badge>
              <span className="ms-2 text-muted-foreground">{isAr ? "يعرض رصيد العميل (نقاط + درهم)" : "Shows balance (points + AED)"}</span>
            </li>
            <li>
              <Badge variant="outline" className="text-[10px]">{isAr ? "فوتر اكتشاف" : "Discover footer"}</Badge>
              <span className="ms-2 text-muted-foreground">{isAr ? "دعوة المُهدى إليه لزيارة الموقع" : "Invites gift recipient to visit the site"}</span>
            </li>
            <li>
              <Badge variant="outline" className="text-[10px]">{isAr ? "بلا فوتر" : "No footer"}</Badge>
              <span className="ms-2 text-muted-foreground">{isAr ? "بعض الرسائل التشغيليّة" : "Some operational messages"}</span>
            </li>
          </ul>
        </section>

        {/* ─── منطق اختيار اللغة ─── */}
        <section className="mt-4 rounded-none border border-primary/30 bg-primary/5 p-4 text-xs leading-relaxed">
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
            🌐 {isAr ? "متى تُرسَل بالعربيّ ومتى بالإنجليزيّ؟" : "When is Arabic vs English sent?"}
          </h2>
          <ol className="ms-4 list-decimal space-y-1.5 text-muted-foreground">
            <li>
              <span className="font-medium text-foreground">
                {isAr ? "تفضيل العميل المحفوظ" : "Saved customer preference"}
              </span>{" "}
              — <code className="rounded bg-muted px-1 py-0.5 text-[10px]">profiles.preferred_language</code>{" "}
              {isAr
                ? "(يُضبَط من لغة الموقع عند التسجيل أو من /account/settings)."
                : "(set from site locale at signup, or /account/settings)."}
            </li>
            <li>
              <span className="font-medium text-foreground">
                {isAr ? "لغة السوق الافتراضيّة" : "Market default"}
              </span>{" "}
              —{" "}
              {isAr
                ? "SA · AE · IQ · OM ← عربيّ · باقي الدول (ROW) ← إنجليزيّ."
                : "SA · AE · IQ · OM → Arabic · other countries (ROW) → English."}
            </li>
            <li>
              <span className="font-medium text-foreground">
                {isAr ? "احتياطيّ أخير" : "Final fallback"}
              </span>{" "}
              — {isAr ? "عربيّ (لغة البراند الأمّ)." : "Arabic (brand default)."}
            </li>
          </ol>
          <p className="mt-3 border-t border-primary/20 pt-2 text-[11px] text-muted-foreground">
            💡{" "}
            {isAr
              ? "لهذا كلّ قالب أدناه يحوي حقلين (عربيّ + إنجليزيّ) — عدّلهما معاً دائماً. النصّ الصحيح يُختار آليّاً وقت الإرسال."
              : "That's why every template below has two fields (AR + EN) — always edit both. The right one is picked automatically at send time."}
          </p>
        </section>

        {/* ─── سجلّ المراقبة ─── */}
        <section className="mt-6 rounded-none border border-border bg-card">
          <div className="border-b border-border/60 px-4 py-3">
            <h2 className="text-sm font-medium">
              {isAr ? "سجلّ القوالب" : "Templates Registry"}
            </h2>
          </div>
          {regLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {Object.entries(groupedRows).map(([group, groupRows]) => (
                <div key={group} className="px-4 py-3">
                  <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {groupLabels[group]?.[isAr ? "ar" : "en"] ?? group}
                  </div>
                  <div className="space-y-2">
                    {groupRows.map((row) => (
                      <RegistryRowItem
                        key={row.key}
                        row={row}
                        isAr={isAr}
                        toggling={
                          toggle.isPending && toggle.variables?.key === row.key
                        }
                        onEdit={handleEditTemplate}
                        onToggle={(enabled) =>
                          toggle.mutate({ key: row.key, enabled })
                        }
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ─── محرّرات النصوص (مقسَّمة على تبويبات) ─── */}
        <section className="mt-10">
          <h2 className="text-lg font-medium">
            {isAr ? "تحرير النصوص" : "Edit text"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {isAr
              ? "اختر تبويباً لرؤية القوالب الخاصّة به فقط. التغييرات تسري خلال ٦٠ ثانية."
              : "Pick a tab to see only its templates. Changes take effect within 60s."}
          </p>

          {tplLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Tabs
              value={activeTab}
              onValueChange={(v) =>
                navigate({ search: { tab: v as AdminMessagesTab }, replace: true })
              }
              className="mt-4"
            >
              <TabsList className="flex w-full flex-wrap justify-start gap-1 bg-muted/40 p-1">
                <TabsTrigger value="gift">{isAr ? "الهديّة" : "Gift"}</TabsTrigger>
                <TabsTrigger value="footer">{isAr ? "الفوترات" : "Footers"}</TabsTrigger>
                <TabsTrigger value="order">{isAr ? "الطلبات" : "Orders"}</TabsTrigger>
                <TabsTrigger value="loyalty">{isAr ? "الولاء والمحفظة" : "Loyalty & Wallet"}</TabsTrigger>
                <TabsTrigger value="ambassador">{isAr ? "السفراء" : "Ambassadors"}</TabsTrigger>
              </TabsList>

              {(["gift", "footer", "order", "loyalty", "ambassador"] as const).map((bucket) => {
                const items = ALL_EDITABLE_TEMPLATES.filter((t) => tplBucket(t.key) === bucket);
                return (
                  <TabsContent key={bucket} value={bucket} className="mt-6 space-y-6">
                    {/* مفتاح فوتر الحملة يظهر فقط في تبويب الفوترات */}
                    {bucket === "footer" ? (
                      <div
                        className={`rounded-none border p-4 transition-colors ${
                          campaignData?.enabled
                            ? "border-amber-500/50 bg-amber-500/10"
                            : "border-border bg-muted/30"
                        }`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-semibold">
                                {isAr ? "🎉 فوتر حملة/مناسبة عامّة" : "🎉 Campaign / occasion footer"}
                              </h3>
                              {campaignData?.enabled ? (
                                <Badge className="bg-amber-500 text-amber-50">
                                  {isAr ? "مفعَّل — يحلّ محلّ كلّ الفوترات" : "ACTIVE — overrides all footers"}
                                </Badge>
                              ) : null}
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {isAr
                                ? "للمناسبات العامّة (رمضان، عيد، يوم وطنيّ، إطلاق). عند التفعيل يُلحَق فوتر الحملة بكلّ الرسائل (واتساب وإيميل) ويُعطَّل تلقائياً فوتر «راجع حسابك» وفوتر المحفظة وفوتر الاكتشاف."
                                : "For seasonal occasions. When enabled, the campaign footer is appended to every message and the account/wallet/discover footers are automatically suppressed."}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            {campaignToggle.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            ) : null}
                            <Switch
                              checked={!!campaignData?.enabled}
                              onCheckedChange={(v) => campaignToggle.mutate(v)}
                              disabled={campaignToggle.isPending}
                              aria-label={isAr ? "تفعيل فوتر الحملة" : "Enable campaign footer"}
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {items.length === 0 ? (
                      <div className="rounded-none border border-dashed border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                        {isAr ? "لا توجد قوالب في هذا التبويب." : "No templates in this tab."}
                      </div>
                    ) : (
                      items.map((tpl) => (
                        <div key={tpl.key} id={`tpl-${tpl.key}`} className="scroll-mt-32">
                          <TemplateEditor
                            meta={tpl}
                            initial={templatesMap.get(tpl.key) ?? { ar: "", en: "" }}
                            isAr={isAr}
                            onSave={(values) => upsert.mutate({ key: tpl.key, ...values })}
                            saving={upsert.isPending && upsert.variables?.key === tpl.key}
                          />
                        </div>
                      ))
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          )}
        </section>
      </div>
    </SiteShell>
  );
}

function formatDate(iso: string | null, isAr: boolean): string {
  if (!iso) return isAr ? "—" : "—";
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  if (minutes < 60) return isAr ? `قبل ${minutes} د` : `${minutes}m ago`;
  if (hours < 24) return isAr ? `قبل ${hours} س` : `${hours}h ago`;
  if (days < 30) return isAr ? `قبل ${days} يوم` : `${days}d ago`;
  return d.toLocaleDateString(isAr ? "ar" : "en");
}

function RegistryRowItem({
  row,
  isAr,
  toggling,
  onEdit,
  onToggle,
}: {
  row: RegistryRow;
  isAr: boolean;
  toggling: boolean;
  onEdit: (templateKey: string) => void;
  onToggle: (enabled: boolean) => void;
}) {
  const footerLabel: Record<string, { ar: string; en: string }> = {
    none: { ar: "بلا فوتر", en: "No footer" },
    generic: { ar: "فوتر عامّ", en: "Generic footer" },
    wallet: { ar: "فوتر بالرصيد", en: "Wallet footer" },
    unsub: { ar: "فوتر إلغاء اشتراك", en: "Unsub footer" },
    discover: { ar: "فوتر اكتشاف", en: "Discover footer" },
  };
  return (
    <div className="flex flex-wrap items-start gap-3 rounded-none border border-border/40 bg-background/50 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">
            {isAr ? row.title_ar : row.title_en}
          </span>
          {row.editable_here ? (
            <Badge variant="outline" className="text-[10px]">
              {isAr ? "يُحرَّر هنا" : "Edits here"}
            </Badge>
          ) : null}
          {!row.enabled ? (
            <Badge variant="destructive" className="text-[10px]">
              {isAr ? "معطَّل" : "Disabled"}
            </Badge>
          ) : null}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {isAr ? row.hint_ar : row.hint_en}
        </p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {row.channels.includes("whatsapp") ? (
            <Badge variant="secondary" className="text-[10px]">📱 {isAr ? "واتساب" : "WhatsApp"}</Badge>
          ) : null}
          {row.channels.includes("email") ? (
            <Badge variant="secondary" className="text-[10px]">✉️ {isAr ? "إيميل" : "Email"}</Badge>
          ) : null}
          <Badge variant="outline" className="text-[10px]">
            {footerLabel[row.footer]?.[isAr ? "ar" : "en"]}
          </Badge>
        </div>
        <dl className="mt-2 grid gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground sm:grid-cols-2">
          <div>
            <dt className="inline font-medium text-foreground/70">{isAr ? "المستلم:" : "Recipient:"}</dt>{" "}
            <dd className="inline">{isAr ? row.recipient_ar : row.recipient_en}</dd>
          </div>
          <div>
            <dt className="inline font-medium text-foreground/70">{isAr ? "متى:" : "When:"}</dt>{" "}
            <dd className="inline">{isAr ? row.trigger_ar : row.trigger_en}</dd>
          </div>
        </dl>
      </div>

      <div className="flex shrink-0 items-center gap-4 text-xs text-muted-foreground">
        <div className="text-end">
          <div className="font-mono text-sm text-foreground">{row.count_30d}</div>
          <div className="text-[10px] uppercase tracking-wide">
            {isAr ? "آخر ٣٠ يوم" : "Last 30d"}
          </div>
        </div>
        <div className="text-end">
          <div className="text-xs">{formatDate(row.last_sent_at, isAr)}</div>
          <div className="text-[10px] uppercase tracking-wide">
            {isAr ? "آخر إرسال" : "Last sent"}
          </div>
        </div>
        {row.editable_here ? (
          <button
            type="button"
            onClick={() => onEdit(row.key)}
            className="inline-flex items-center gap-1 text-xs underline hover:text-foreground"
          >
            <Pencil className="h-3 w-3" />
            {isAr ? "تحرير" : "Edit"}
          </button>
        ) : (
          <Link
            to={row.edit_path}
            className="inline-flex items-center gap-1 text-xs underline hover:text-foreground"
          >
            <ExternalLink className="h-3 w-3" />
            {isAr ? "تحرير" : "Edit"}
          </Link>
        )}
        <div className="flex items-center gap-1.5">
          {toggling ? (
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          ) : null}
          <Switch
            checked={row.enabled}
            onCheckedChange={onToggle}
            disabled={toggling}
            aria-label={isAr ? "تفعيل" : "Enabled"}
          />
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({
  meta,
  initial,
  isAr,
  onSave,
  saving,
}: {
  meta: TemplateMeta;
  initial: { ar: string; en: string };
  isAr: boolean;
  onSave: (values: { ar: string; en: string }) => void;
  saving: boolean;
}) {
  const [ar, setAr] = React.useState(initial.ar);
  const [en, setEn] = React.useState(initial.en);
  React.useEffect(() => {
    setAr(initial.ar);
    setEn(initial.en);
  }, [initial.ar, initial.en]);

  const dirty = ar !== initial.ar || en !== initial.en;

  return (
    <section className="rounded-none border border-border bg-card p-5">
      <div className="mb-3">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <CategoryBadge category={meta.category} isAr={isAr} />
        </div>
        <h3 className="font-medium">{isAr ? meta.title_ar : meta.title_en}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {isAr ? meta.hint_ar : meta.hint_en}
        </p>
        {meta.placeholders.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {meta.placeholders.map((p) => (
              <code
                key={p}
                className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-muted-foreground"
              >
                {p}
              </code>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            العربيّة
          </Label>
          <Textarea
            value={ar}
            onChange={(e) => setAr(e.target.value)}
            rows={5}
            dir="rtl"
            className="resize-y font-arabic text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            English
          </Label>
          <Textarea
            value={en}
            onChange={(e) => setEn(e.target.value)}
            rows={5}
            dir="ltr"
            className="resize-y text-sm"
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          size="sm"
          disabled={!dirty || saving}
          onClick={() => onSave({ ar, en })}
        >
          {saving ? (
            <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="me-1.5 h-3.5 w-3.5" />
          )}
          {isAr ? "حفظ" : "Save"}
        </Button>
      </div>
    </section>
  );
}
